import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { SUBMIT } from "@/constants/testIds";

const EMPTY = {
  title_fr: "", title_en: "", country: "Haiti", type: "main", difficulty: "medium", time_minutes: 30,
  image: "", summary_fr: "", summary_en: "",
  ingredients_fr: "", ingredients_en: "", steps_fr: "", steps_en: "",
};

export default function SubmitRecipePage() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const isEdit = Boolean(recipeId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!isEdit || !user) return;
    api.getMyRecipe(recipeId).then((r) => {
      setForm({
        title_fr: r.title.fr || "", title_en: r.title.en || "",
        country: r.country, type: r.type, difficulty: r.difficulty,
        time_minutes: r.time_minutes,
        image: r.image && !r.image.includes("photo-1490645935967") ? r.image : "",
        summary_fr: r.summary?.fr || "", summary_en: r.summary?.en || "",
        ingredients_fr: (r.ingredients?.fr || []).join("\n"),
        ingredients_en: (r.ingredients?.en || []).join("\n"),
        steps_fr: (r.steps?.fr || []).join("\n"),
        steps_en: (r.steps?.en || []).join("\n"),
      });
    }).catch(() => setError("Recipe not found"));
  }, [isEdit, recipeId, user]);

  if (!loading && !user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif-display text-4xl mb-4">{t.auth.required}</h1>
        <Link to="/auth" className="btn-navy">{t.nav.login}</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        title_fr: form.title_fr, title_en: form.title_en, country: form.country, type: form.type,
        difficulty: form.difficulty, time_minutes: parseInt(form.time_minutes) || 30, image: form.image,
        summary_fr: form.summary_fr, summary_en: form.summary_en,
        ingredients_fr: form.ingredients_fr.split("\n").map((s) => s.trim()).filter(Boolean),
        ingredients_en: form.ingredients_en.split("\n").map((s) => s.trim()).filter(Boolean),
        steps_fr: form.steps_fr.split("\n").map((s) => s.trim()).filter(Boolean),
        steps_en: form.steps_en.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.updateMyRecipe(recipeId, payload);
      } else {
        await api.submitRecipe(payload);
      }
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (done) {
    return (
      <div data-testid={SUBMIT.success} className="max-w-2xl mx-auto px-6 py-32 text-center">
        <p className="eyebrow mb-4">№ 06</p>
        <h1 className="font-serif-display text-5xl mb-6">{t.submit.success}</h1>
        <p className="text-[color:var(--ink-2)] mb-12 leading-relaxed">{t.submit.successSub}</p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link data-testid={SUBMIT.backProfile} to="/profile" className="btn-navy">{t.submit.backProfile}</Link>
          <button data-testid={SUBMIT.newOne} onClick={() => { setDone(false); navigate("/submit"); }} className="btn-outline">{t.submit.newOne}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 pt-16 pb-24">
      <p className="eyebrow mb-4">№ 06</p>
      <h1 className="font-serif-display text-5xl mb-12">{isEdit ? t.submit.editTitle : t.submit.title}</h1>

      <form onSubmit={submit} className="space-y-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div><label className="label-line">{t.submit.titleFr}</label><input data-testid={SUBMIT.titleFr} required className="input-line" value={form.title_fr} onChange={set("title_fr")} /></div>
          <div><label className="label-line">{t.submit.titleEn}</label><input data-testid={SUBMIT.titleEn} required className="input-line" value={form.title_en} onChange={set("title_en")} /></div>
          <div><label className="label-line">{t.submit.country}</label><input data-testid={SUBMIT.country} required className="input-line" value={form.country} onChange={set("country")} /></div>
          <div><label className="label-line">{t.submit.type}</label>
            <select data-testid={SUBMIT.type} value={form.type} onChange={set("type")} className="input-line">
              {["soup","main","side","starter","street","drink","dessert","condiment"].map((x) => <option key={x} value={x}>{t.types[x]}</option>)}
            </select>
          </div>
          <div><label className="label-line">{t.submit.difficulty}</label>
            <select data-testid={SUBMIT.difficulty} value={form.difficulty} onChange={set("difficulty")} className="input-line">
              {["easy","medium","hard"].map((x) => <option key={x} value={x}>{t.difficulties[x]}</option>)}
            </select>
          </div>
          <div><label className="label-line">{t.submit.time}</label><input data-testid={SUBMIT.time} type="number" min="5" className="input-line" value={form.time_minutes} onChange={set("time_minutes")} /></div>
        </div>

        <div><label className="label-line">{t.submit.image}</label><input data-testid={SUBMIT.image} className="input-line" value={form.image} onChange={set("image")} placeholder="https://..." /></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div><label className="label-line">{t.submit.summaryFr}</label><textarea data-testid={SUBMIT.summaryFr} rows={3} className="input-line" value={form.summary_fr} onChange={set("summary_fr")} /></div>
          <div><label className="label-line">{t.submit.summaryEn}</label><textarea data-testid={SUBMIT.summaryEn} rows={3} className="input-line" value={form.summary_en} onChange={set("summary_en")} /></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div><label className="label-line">{t.submit.ingredientsFr}</label><textarea data-testid={SUBMIT.ingredientsFr} rows={6} className="input-line" value={form.ingredients_fr} onChange={set("ingredients_fr")} /></div>
          <div><label className="label-line">{t.submit.ingredientsEn}</label><textarea data-testid={SUBMIT.ingredientsEn} rows={6} className="input-line" value={form.ingredients_en} onChange={set("ingredients_en")} /></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div><label className="label-line">{t.submit.stepsFr}</label><textarea data-testid={SUBMIT.stepsFr} rows={6} className="input-line" value={form.steps_fr} onChange={set("steps_fr")} /></div>
          <div><label className="label-line">{t.submit.stepsEn}</label><textarea data-testid={SUBMIT.stepsEn} rows={6} className="input-line" value={form.steps_en} onChange={set("steps_en")} /></div>
        </div>

        {error && <p className="text-sm text-[color:var(--red)]">{error}</p>}
        <button data-testid={SUBMIT.submit} disabled={busy} className="btn-navy">{busy ? "..." : (isEdit ? t.submit.saveEdit : t.submit.submit)}</button>
      </form>
    </div>
  );
}
